/**
 * LA TUILE TELLE QUE LA CAISSE LA DESSINE — toutes ses versions, pour l'aperçu et pour le composeur du studio.
 *
 * Des CARRÉS pour le texte seul et les pictos : un dossier montre son picto en haut, son nom et sa pastille (« 12
 * articles ») en bas ; un article son nom et son prix. Ce qui porte une photo : la photo entière d'un dossier (le nom
 * posé dessus), un bandeau réduit (le nom et la pastille dessous), la photo à gauche du nom ; la vignette d'un article
 * (bandeau 16:10), réduite, ou sa photo à gauche. La VERSION, la HAUTEUR et la PLACE viennent de la charte
 * (`quickTileVariant`, `quickTileHeight`, `quickGridLayout`), que la caisse lit aussi : ce que le gérant compose et
 * prévisualise est ce que le caissier touche.
 * ⚠️ LES RANGÉES SONT CALCULÉES, JAMAIS LAISSÉES AU NAVIGATEUR : il répartit autrement le manque d'une tuile haute, et
 *    le studio montrerait un autre écran que la caisse.
 * ⚠️ LES MESURES SONT CELLES DE LA TABLETTE (`QUICK_TILE_BODY`, en pixels), jamais l'échelle typographique du
 *    back-office : la hauteur d'une tuile se calcule à partir d'elles.
 * ⚠️ LES VOILES SONT LE PAPIER DU THÈME (`webPaperVeil`) : l'encre posée dessus bascule en sombre, un blanc non.
 */
import { useLayoutEffect, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { Icon } from '@orgazone/ui-web';
import {
  QUICK_TILE_BODY, QUICK_VEIL, isQuickSquare, noPhotoHatchCss, quickBandIn, quickDrivingClass, quickFitVariant, quickFolderRowHeight,
  quickGridColumns, quickGridLayout, quickNameLines, quickPillForm, quickTextWidthEstimate, quickTileHeight, quickTileWidth, webPaperVeil,
  type QuickTileVariant,
} from '@orgazone/ui-tokens';
import type { QuickTileFace } from '@orgazone/sync-contract';
import type { StudioArticle } from '../api';
import { t } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { knownIcon, photoStyle, type TileFraming, type TileTone } from './quick-studio-look';
import { formatPrice } from './quick-studio-model';

const B = QUICK_TILE_BODY;

/**
 * LA LARGEUR UTILE D'UNE GRILLE DU STUDIO — mesurée sur sa boîte, marges déduites.
 * ⚠️ `clientWidth` ET PAS LA BOÎTE ENGLOBANTE : l'aperçu est réduit d'un bloc (`scale`), et ses cases se calculent dans
 *    les pixels de la tablette ; une transformation ne change pas `clientWidth`.
 * - `fallback` : la largeur supposée avant la première mesure — et dans un rendu sans mise en page (banc).
 * ⚠️ UNE AUTRE LARGEUR SUPPOSÉE EST UN AUTRE APPAREIL (l'aperçu passe de la tablette au téléphone) : l'ancienne mesure
 *    ne vaut plus, on repart de la nouvelle supposition et on remesure.
 */
export function useGridWidth(ref: RefObject<HTMLElement | null>, fallback: number): number {
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    setWidth(fallback);
    const el = ref.current;
    if (!el) return undefined;
    const measure = (): void => {
      const cs = getComputedStyle(el);
      const w = el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      if (w > 0) setWidth(w);
    };
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [ref, fallback]);
  return width;
}

/** Une cellule d'un niveau : son encombrement et sa version. */
export interface StudioCell { w: number; h: number; variant: QuickTileVariant }

/** La place d'une cellule dans sa grille : sa case CSS, sa hauteur et sa largeur réelles, la version qui y tient. */
export interface StudioPlace { style: CSSProperties; height: number; width: number; variant: QuickTileVariant }

/**
 * LA GRILLE D'UN NIVEAU, À LA MANIÈRE DE LA CAISSE — ses colonnes (réglées par la plus grande classe du niveau), ses
 * rangées (chacune à la hauteur de sa tuile la plus haute) et la place de chaque cellule, en grille CSS explicite.
 * ⚠️ LA VERSION DESSINÉE EST CELLE QUI TIENT DANS LA LARGEUR DE LA TUILE (`quickFitVariant`) : la photo posée à gauche
 *    du nom s'efface quand le nom n'aurait plus sa place, exactement comme en caisse. `place(i).variant` la donne au
 *    rendu — la dessiner autrement que la hauteur prise ici ferait une tuile qui ne remplit pas sa rangée.
 * ⚠️ UNE RANGÉE COMPACTE GARDE SES CARRÉS (`quickGridLayout`, `narrow`) : la tuile se cale à gauche de sa case, au
 *    côté de sa rangée.
 * ⚠️ CASES PLAFONNÉES (`capped`), LES COLONNES ONT UNE LARGEUR FIXE : la place libre reste à droite, comme en caisse.
 * - `add` : la case « + Ajouter » du composeur — la première case libre (le placement de la grille), à la hauteur de sa
 *   rangée (celle de la dernière dans une rangée neuve), carrée dans une rangée compacte. Elle ne règle rien.
 */
export function studioGrid(cells: readonly StudioCell[], width: number, cols: number | null): {
  /** Les colonnes que la grille dessine, et la largeur de l'une — ce que l'aperçu annonce. */
  cols: number;
  unitW: number;
  style: CSSProperties;
  place: (i: number) => StudioPlace;
  add: CSSProperties;
} {
  const driving = quickDrivingClass(cells.map((c) => c.variant));
  const g = quickGridColumns(width, cols, driving);
  const fitted = cells.map((c) => quickFitVariant(c.variant, quickTileWidth(Math.min(c.w, g.cols), g.unitW, g.gap)));
  const layout = quickGridLayout(cells.map((c, i) => ({
    w: c.w, h: c.h, height: quickTileHeight(fitted[i]!, { unitW: g.unitW, h: c.h, driving }),
    square: isQuickSquare(fitted[i]!), empty: fitted[i] === 'empty',
  })), g.cols, g.unitW, g.gap);
  const taken = new Set(layout.slots.flatMap((s) => {
    const out: string[] = [];
    for (let r = s.row; r < s.row + s.h; r += 1) for (let c = s.col; c < s.col + s.w; c += 1) out.push(`${r}:${c}`);
    return out;
  }));
  let free = 0;
  while (taken.has(`${Math.floor(free / g.cols)}:${free % g.cols}`)) free += 1;
  const addRow = Math.floor(free / g.cols);
  const ref = Math.min(addRow, layout.rows.length - 1);
  const addH = Math.max(B.touch, ref >= 0 ? layout.rows[ref]! : quickTileHeight('compact', { unitW: g.unitW, h: 1, driving }));
  const addSquare = ref >= 0 && layout.narrow.some((n, i) => n && layout.slots[i]!.row === ref);
  return {
    cols: g.cols,
    unitW: g.unitW,
    style: {
      display: 'grid', gap: g.gap, alignContent: 'start',
      gridTemplateColumns: g.capped ? `repeat(${g.cols}, ${g.unitW}px)` : `repeat(${g.cols}, minmax(0, 1fr))`,
      gridTemplateRows: layout.rows.map((r) => `${r}px`).join(' ') || undefined,
    },
    place: (i) => {
      const s = layout.slots[i];
      const rect = layout.rects[i];
      if (!s || !rect) return { style: {}, height: 0, width: 0, variant: cells[i]?.variant ?? 'compact' };
      return {
        style: {
          gridColumn: `${s.col + 1} / span ${s.w}`, gridRow: `${s.row + 1} / span ${s.h}`,
          ...(layout.narrow[i] ? { justifySelf: 'start', width: rect.width } : null),
        },
        height: rect.height, width: rect.width, variant: fitted[i]!,
      };
    },
    add: {
      gridColumn: `${(free % g.cols) + 1} / span 1`, gridRow: `${addRow + 1} / span 1`, height: addH,
      ...(addSquare ? { justifySelf: 'start', width: addH } : null),
    },
  };
}

/** Ce qu'une tuile montre — de quoi dessiner sa version. */
export interface StudioTileContent {
  variant: QuickTileVariant;
  name: string;
  /**
   * Un dossier ou une source : les articles qu'il ouvre, tous niveaux confondus (`deepArticleCount`) — `null` tant que
   * le compte se lit.
   */
  count?: { n: number | null };
  /** Un article : son prix, sa marque, son code et sa taille déjà nommée. */
  article?: { price: string; brand: string; code: string; size: string | null };
  tone: TileTone | null;
  face: QuickTileFace;
  framing: TileFraming;
  tall: boolean;
  /** La hauteur de la tuile dans sa rangée : un bandeau prend tout ce qui n'est pas le texte. */
  height: number;
  /** La largeur de la tuile — la place que sa pastille trouve. */
  width: number;
  /** Ce que le composeur ajoute à côté de la pastille ou du prix — un état (« Pas en caisse », « −3 »). */
  note?: ReactNode;
  /** Le composeur : ses boutons, et la place qu'ils prennent au texte (`studioTileControls`) ; absent, l'aperçu. */
  controls?: StudioTileControls;
  /** Le composeur : « Photo » est choisi et aucune photo n'existe — sa place se dessine, et le dit. */
  photoToAdd?: boolean;
}

/** Ce qu'un article montre sur sa tuile : son prix, sa marque, le code que la caisse affiche, sa taille. */
export function articleContent(a: StudioArticle): NonNullable<StudioTileContent['article']> {
  return {
    price: formatPrice(a.priceCents), brand: a.brand ?? '',
    // ⚠️ LE CODE EST CELUI QUE LA CAISSE MONTRE : le code-barres d'abord, le code article à défaut.
    code: a.gencode ?? a.articleCode ?? '',
    size: a.size ? `${fieldLabel('size')} ${a.size}` : null,
  };
}

/** Les versions dont une photo occupe une bande de la tuile, le texte dessous. */
const BANDED: ReadonlySet<QuickTileVariant> = new Set(['vignette', 'vignetteReduced', 'photoReduced']);
/** Les carrés à picto : le picto en haut, le nom et ce qu'ils contiennent calés en bas. */
const PICTO_SQUARE: ReadonlySet<QuickTileVariant> = new Set(['icon', 'largeIcon', 'articleIcon', 'articleLarge']);

/**
 * LE CADRE D'UNE TUILE — son filet, son rayon, son fond et la disposition de son contenu ; la sélection et le survol se
 * posent par-dessus.
 * ⚠️ UNE VIGNETTE EST SUR PAPIER, la photo d'un dossier sur sa teinte (elle se voit tant que l'image arrive), le reste
 *    sur sa teinte, sinon le neutre d'un dossier ou le papier d'un article.
 * ⚠️ AU COMPOSEUR, LE TEXTE SEUL DESCEND sous la rangée de ses boutons (`controls.textTop`).
 */
export function studioTileFrame(c: Pick<StudioTileContent, 'variant' | 'tone' | 'controls'>, level: boolean): CSSProperties {
  const v = c.variant;
  const vignette = v === 'vignette' || v === 'vignetteReduced';
  const square = PICTO_SQUARE.has(v);
  const text = v === 'compact' || v === 'text';
  const padding = v === 'photo' || BANDED.has(v) ? 0
    : text ? `${c.controls?.textTop ?? B.compact.padY}px ${B.compact.padX}px ${B.compact.padY}px`
    : B.pad;
  return {
    boxSizing: 'border-box', minWidth: 0, minHeight: 0, overflow: 'hidden', textAlign: 'left', position: 'relative',
    display: 'flex', flexDirection: v === 'photoLeft' ? 'row' : 'column',
    justifyContent: BANDED.has(v) ? 'flex-start' : square ? 'space-between' : 'center',
    alignItems: v === 'photoLeft' || text || v === 'empty' ? 'center' : 'stretch',
    gap: square ? (v === 'articleIcon' || v === 'articleLarge' ? B.square.articleGap : B.square.gap) : v === 'photoLeft' ? B.box.gap : 0,
    padding, borderRadius: B.radius, border: `${B.border}px solid ${c.tone?.bd ?? 'var(--ink-200)'}`,
    background: vignette ? 'var(--paper)' : c.tone?.bg ?? (level ? 'var(--ink-100)' : 'var(--paper)'),
  };
}

/** Une place de bouton dans la tuile, en pixels depuis ses bords. */
export interface ControlSpot { top?: number; bottom?: number; left?: number; right?: number }

/** Où le composeur pose ses boutons sur une tuile, et la place que chacun retire au contenu. */
export interface StudioTileControls {
  /** La poignée : toujours en haut à gauche. */
  grip: ControlSpot;
  /** L'ouverture du niveau (`null` : la tuile n'ouvre rien). */
  open: ControlSpot | null;
  /** La place rendue au nom d'une photo à gauche, ou à la pastille d'un bandeau réduit, au bout de leur ligne. */
  insetRight: number;
  /** La place rendue au mot d'une case vide, que la poignée précède (une case vide n'a pas de contenu de tuile). */
  insetLeft: number;
  /** La place rendue à l'étiquette posée en bas d'une photo (le › du bas). */
  labelInsetRight: number;
  /** La place que le › du bas prend à la ligne de la pastille. */
  pillReserve: number;
  /** La marge du haut d'une tuile de texte seul, sous la rangée des boutons. */
  textTop: number;
  /** Le code-barres d'un bandeau se décale à droite de la poignée. */
  codeLeft: number;
}

/** Le bouton de retrait (22 px), l'ouverture du niveau (24 px, bordée), la poignée (16 px), leur marge au bord et entre eux. */
export const STUDIO_CONTROL = { button: 22, open: 24, grip: 16, edge: 6, gap: 4, openBottom: 8 } as const;

/**
 * LES BOUTONS DU COMPOSEUR SUR UNE TUILE — le retrait (✕), l'ouverture du niveau (›) et la poignée — sans qu'aucun
 * tombe sur un autre ni sur du texte.
 * ⚠️ LA POIGNÉE EST TOUJOURS EN HAUT À GAUCHE (maquette : le même geste sur toutes les tuiles) ; sur un bandeau
 *    d'article, le code-barres se décale à sa droite plutôt que de la chasser.
 * ⚠️ SOUS 62 PX, le › passe à côté du ✕, sur la rangée du haut : posés aux deux coins d'une tuile basse, ils se
 *    recouvraient. Au-dessus, il est en bas à droite, et la ligne de la pastille lui laisse sa place (`pillReserve`).
 * ⚠️ LE COMPOSEUR SEUL : l'aperçu de la caisse n'a pas de boutons, et garde les marges de la caisse.
 */
export function studioTileControls(o: { variant: QuickTileVariant; height: number; level: boolean }): StudioTileControls {
  const C = STUDIO_CONTROL;
  const textStart = B.border + B.pad;
  // ⚠️ LE SEUIL DE LA MAQUETTE (62 px) : deux boutons de 22 l'un sur l'autre et leurs marges — calculé, jamais recopié.
  const stacked = o.height >= C.edge + C.button + C.gap + C.button + C.openBottom;
  const openOnTop = o.level && !stacked;
  const topButtons = openOnTop ? 2 : 1;
  const topRowWidth = C.edge + topButtons * C.button + (topButtons - 1) * C.gap;
  return {
    grip: { top: C.edge + (C.button - C.grip) / 2, left: C.edge },
    open: !o.level ? null : openOnTop
      ? { top: C.edge, right: C.edge + C.button + C.gap }
      : { bottom: C.openBottom, right: C.openBottom },
    insetRight: topRowWidth + C.gap - textStart,
    insetLeft: o.variant === 'empty' ? C.grip : 0,
    labelInsetRight: o.level && !openOnTop ? C.button + C.gap : 0,
    pillReserve: o.level && !openOnTop ? (PICTO_SQUARE.has(o.variant) ? C.open + C.gap : C.button + C.gap) : 0,
    textTop: C.edge + C.button - 2,
    codeLeft: C.edge + C.grip + C.edge,
  };
}

/**
 * LE NOM SUR UNE LIGNE, COUPÉ PAR « … » — sous un bandeau ou dans une rangée (`QUICK_TILE_BODY.nameLines`).
 * ⚠️ `maxWidth` EST INDISPENSABLE : dans une colonne alignée à gauche, un texte qui ne passe pas à la ligne prend sa
 *    largeur NATURELLE et déborde sous le bouton, sans « … ».
 */
const oneLine: CSSProperties = { display: 'block', maxWidth: '100%', boxSizing: 'border-box', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/**
 * LE NOM SUR `lines` LIGNES, LA DERNIÈRE COUPÉE PAR « … » (`quickNameLines`, charte) — une ligne, le style ci-dessus.
 * ⚠️ `overflow-wrap: anywhere` COMME LA MAQUETTE : les mots passent à la ligne entre eux, et seul un mot plus long que
 *    la ligne entière se coupe — sinon il déborderait, caché, sans « … ».
 * - `balance` : des lignes de longueurs voisines (maquette : grand picto, photo à gauche, texte seul).
 */
function nameLines(lines: number, balance = false): CSSProperties {
  if (lines <= 1) return oneLine;
  return {
    display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, maxWidth: '100%', boxSizing: 'border-box',
    whiteSpace: 'normal', overflowWrap: 'anywhere', overflow: 'hidden', ...(balance ? { textWrap: 'balance' } : null),
  };
}

/** Le caisson d'un picto — voilé sur une tuile teintée, neutre sinon. */
function IconBox({ icon, tone, box, glyph }: { icon: string; tone: TileTone | null; box: number; glyph: number }) {
  return (
    <span aria-hidden data-qs-icon style={{ flex: `0 0 ${box}px`, width: box, height: box, borderRadius: B.iconRadius, background: tone ? webPaperVeil(QUICK_VEIL.icon) : 'var(--ink-200)', color: tone?.fg ?? 'var(--ink-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={knownIcon(icon)} size={glyph} stroke={B.glyphStroke} />
    </span>
  );
}

/**
 * Une photo cadrée (celle du studio) ou centrée, sinon les hachures — dans la case qui la contient.
 * ⚠️ UNE PHOTO À AJOUTER se dit par-dessus les hachures (`photoToAdd`) ; dans une petite case, trop petite pour une
 *    phrase, le picto seul — la phrase passe en infobulle. `bottom` laisse libre l'étiquette d'une photo entière.
 */
function PhotoFill({ c, small = false, bottom = 0 }: { c: StudioTileContent; small?: boolean; bottom?: number }) {
  if (c.face.kind === 'photo') return <span data-qs-photo style={{ position: 'absolute', inset: 0 }}><span style={photoStyle(c.framing, c.face.uri)} /></span>;
  return (
    <span data-qs-photo data-qs-no-photo style={{ position: 'absolute', inset: 0, background: noPhotoHatchCss() }}>
      {c.photoToAdd && (
        <span data-qs-photo-to-add title={t('Ajouter une photo')} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: 'var(--ink-600)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: small ? 24 : 28, height: small ? 24 : 28, borderRadius: 'var(--r-xs)', background: webPaperVeil(QUICK_VEIL.code) }}>
            <Icon name="image" size={small ? 13 : 15} />
          </span>
          {!small && (
            <span style={{ padding: '1px 6px', borderRadius: 'var(--r-xs)', background: webPaperVeil(QUICK_VEIL.code), fontSize: 'var(--t-micro)', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {t('Ajouter une photo')}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/** Le caisson à gauche du nom : la photo (des hachures sans photo). */
function LeftPhoto({ c }: { c: StudioTileContent }) {
  return (
    <span aria-hidden data-qs-left-photo style={{ position: 'relative', flex: `0 0 ${B.box.size}px`, width: B.box.size, height: B.box.size, borderRadius: B.iconRadius, overflow: 'hidden', background: c.tone?.bg ?? 'var(--ink-100)' }}>
      <PhotoFill c={c} small />
    </span>
  );
}

/** Le nom d'un dossier ou d'une source. */
function LevelName({ c, insetRight, size = B.name.size, line = B.name.line, center = false, lines = 1, balance = false }: {
  c: StudioTileContent; insetRight?: number; size?: number; line?: number; center?: boolean; lines?: number; balance?: boolean;
}) {
  return (
    <span data-qs-name style={{ ...nameLines(lines, balance), minWidth: 0, fontSize: size, lineHeight: `${line}px`, fontWeight: 700, color: 'var(--ink-900)', paddingRight: insetRight, textAlign: center ? 'center' : undefined }}>
      {c.name}
    </span>
  );
}

/** Le contexte qui mesure les libellés — créé une fois ; `null` hors navigateur (jsdom n'a pas de canevas). */
let measuring: OffscreenCanvasRenderingContext2D | null | undefined;

/**
 * LA LARGEUR RÉELLE D'UN LIBELLÉ EN GRAS, à la police de la page — la maquette mesure (`txtW`), le studio aussi : une
 * estimation large fait passer « 12 articles » en « 12 art. » dans une tuile où il tient. Hors navigateur, l'estimation.
 */
function measureBold(label: string, size: number): number {
  if (measuring === undefined) measuring = typeof OffscreenCanvas === 'undefined' ? null : new OffscreenCanvas(1, 1).getContext('2d');
  if (!measuring) return quickTextWidthEstimate(label, size);
  const family = typeof document === 'undefined' ? '' : getComputedStyle(document.body).fontFamily;
  measuring.font = `700 ${size}px ${family || 'sans-serif'}`;
  return Math.ceil(measuring.measureText(label).width);
}

/** Ce que dit la pastille : les articles en toutes lettres, « vide », ou « … » tant que le compte se lit. */
function countLabel(n: number | null): string {
  if (n === null) return '…';
  return n === 0 ? t('vide') : t('{n} article', { n });
}

/**
 * LA PASTILLE DU COMPTE — « 12 articles » en toutes lettres, resserrée quand elle ne tient pas (`quickPillForm`) ; au
 * composeur seulement, en dernier recours, « 12 art. » (son › du bas lui prend de la place que la caisse n'a pas).
 * ⚠️ « … » TANT QUE LE COMPTE SE LIT, jamais un zéro : une source pas encore lue n'est pas vide.
 */
function CountPill({ c, room, padX = B.pill.padX, insetRight }: { c: StudioTileContent; room: number; padX?: number; insetRight?: number }) {
  if (!c.count) return null;
  const n = c.count.n;
  const full = countLabel(n);
  const short = c.controls && n !== null && n > 0 ? t('{n} art.', { n }) : null;
  const form = quickPillForm(full, short, room - (c.controls?.pillReserve ?? 0), padX, measureBold);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none', maxWidth: '100%', marginRight: insetRight }}>
      <span data-qs-pill title={full} style={{ height: B.pill.height, padding: `0 ${form.padX}px`, borderRadius: B.pill.height / 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: webPaperVeil(QUICK_VEIL.icon), color: c.tone?.fg ?? 'var(--ink-700)', fontSize: form.size, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
        {form.short && short !== null ? short : full}
      </span>
      {c.note}
    </span>
  );
}

/** La place intérieure d'une tuile : sa largeur sans ses marges ni son filet. */
function innerWidth(c: StudioTileContent): number {
  return c.width - 2 * (B.pad + B.border);
}

/** Le prix d'un article, et la taille à côté (ou l'état que le composeur y pose) — écartés sur une vignette. */
function PriceRow({ c, spread = false }: { c: StudioTileContent; spread?: boolean }) {
  const a = c.article;
  if (!a) return null;
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: spread ? 'space-between' : 'flex-start', gap: 6, height: B.price.line, marginTop: B.price.gap, whiteSpace: 'nowrap', flex: 'none' }}>
      <span style={{ fontSize: B.price.size, lineHeight: `${B.price.line}px`, fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{a.price}</span>
      {c.note ?? (a.size ? <span style={{ fontSize: B.sizeLabel.size, color: 'var(--ink-400)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.size}</span> : null)}
    </span>
  );
}

/** Le nom d'un article. */
function ArticleName({ c, insetRight, lines = 1 }: { c: StudioTileContent; insetRight?: number; lines?: number }) {
  return (
    <span data-qs-name style={{ ...nameLines(lines), minWidth: 0, fontSize: B.articleName.size, lineHeight: `${B.articleName.line}px`, fontWeight: 500, color: 'var(--ink-900)', paddingRight: insetRight }}>
      {c.name}
    </span>
  );
}

/** La marque d'un article, au-dessus de son nom (vignettes et photo à gauche). */
function Brand({ c }: { c: StudioTileContent }) {
  const vt = B.vignetteText;
  return (
    <span style={{ display: 'block', height: vt.brand, lineHeight: `${vt.brand}px`, marginBottom: vt.brandGap, fontSize: vt.brandSize, fontWeight: 600, color: 'var(--teal-fg)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {c.article?.brand ?? ''}
    </span>
  );
}

/** Le bandeau d'une tuile : la photo, sinon les hachures ; le code-barres d'un article par-dessus. */
function Band({ c, variant }: { c: StudioTileContent; variant: 'vignette' | 'vignetteReduced' | 'photoReduced' }) {
  const height = quickBandIn(variant, c.height);
  const code = variant === 'vignette' ? B.code : B.codeReduced;
  return (
    <span style={{ position: 'relative', display: 'block', flex: `1 0 ${height}px`, minHeight: height, background: c.tone?.bg ?? 'var(--ink-100)', overflow: 'hidden' }}>
      <PhotoFill c={c} small={variant !== 'vignette'} />
      {c.article?.code && (
        <span style={{ position: 'absolute', top: code.inset, left: c.controls?.codeLeft ?? code.inset, padding: `${code.padY}px ${code.padX}px`, borderRadius: 6, background: webPaperVeil(QUICK_VEIL.code), fontFamily: 'var(--font-mono)', fontSize: code.size, fontWeight: 600, color: 'var(--ink-700)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          {c.article.code}
        </span>
      )}
    </span>
  );
}

/** LE CONTENU D'UNE TUILE DANS SA VERSION — à poser dans le cadre `studioTileFrame`. */
export function StudioTileBody(c: StudioTileContent) {
  const L = B.label;
  const room = innerWidth(c);
  switch (c.variant) {
    case 'photo':
      // La photo entière ; le nom et le compte sur une étiquette au papier du thème, comme le code-barres d'une vignette.
      return (
        <>
          <PhotoFill c={c} bottom={B.name.line * B.nameLines + 2 * (L.padY + L.inset)} />
          <span data-qs-label style={{ position: 'absolute', left: L.inset, bottom: L.inset, maxWidth: `calc(100% - ${2 * L.inset + (c.controls?.labelInsetRight ?? 0)}px)`, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: L.gap, padding: `${L.padY}px ${L.padX}px`, borderRadius: L.radius, background: webPaperVeil(QUICK_VEIL.code) }}>
            <LevelName c={c} />
            {c.count && (
              <span style={{ flex: 'none', fontSize: L.countSize, fontWeight: 600, color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {countLabel(c.count.n)}
              </span>
            )}
            {c.note}
          </span>
        </>
      );
    case 'photoReduced':
      // Un bandeau fin de photo, puis le nom et la pastille sur une rangée, aux couleurs de la tuile.
      return (
        <>
          <Band c={c} variant="photoReduced" />
          <span style={{ display: 'flex', alignItems: 'center', gap: B.box.gap, height: quickFolderRowHeight(), padding: `0 ${B.pad}px`, flex: 'none', minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0, display: 'flex' }}><LevelName c={c} /></span>
            <CountPill c={c} room={room / 2} insetRight={c.controls?.insetRight} />
          </span>
        </>
      );
    case 'vignette':
    case 'vignetteReduced': {
      const vt = B.vignetteText;
      return (
        <>
          <Band c={c} variant={c.variant} />
          <span style={{ display: 'flex', flexDirection: 'column', padding: `${vt.top}px ${B.pad}px ${vt.bottom}px`, flex: 'none' }}>
            <Brand c={c} />
            <ArticleName c={c} />
            <PriceRow c={c} spread />
          </span>
        </>
      );
    }
    case 'photoLeft':
      return (
        <>
          <LeftPhoto c={c} />
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: B.square.largeNameGap }}>
            <LevelName c={c} insetRight={c.controls?.insetRight} lines={quickNameLines(c.variant, c.height)} balance />
            <CountPill c={c} room={room - B.box.size - B.box.gap} />
          </span>
        </>
      );
    case 'thumb':
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: B.box.gap, minWidth: 0 }}>
          <LeftPhoto c={c} />
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Brand c={c} />
            <ArticleName c={c} insetRight={c.controls?.insetRight} />
            <PriceRow c={c} />
          </span>
        </span>
      );
    case 'icon':
    case 'largeIcon': {
      const large = c.variant === 'largeIcon';
      return (
        <>
          {c.face.kind === 'icon' && <IconBox icon={c.face.icon} tone={c.tone} box={large ? B.box.size : B.icon.box} glyph={large ? B.box.glyph : B.icon.glyph} />}
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'flex-start', gap: large ? B.square.largeNameGap : B.square.nameGap }}>
            <LevelName c={c} lines={quickNameLines(c.variant, c.height)} balance={large} />
            <CountPill c={c} room={room} />
          </span>
        </>
      );
    }
    case 'articleIcon':
    case 'articleLarge': {
      const large = c.variant === 'articleLarge';
      return (
        <>
          {c.face.kind === 'icon' && <IconBox icon={c.face.icon} tone={c.tone} box={large ? B.box.size : B.articleIcon.box} glyph={large ? B.box.glyph : B.articleIcon.glyph} />}
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <ArticleName c={c} lines={quickNameLines(c.variant, c.height)} />
            <PriceRow c={c} />
          </span>
        </>
      );
    }
    case 'text': {
      const T = B.textOnly;
      const a = c.article;
      return (
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: T.gap, flex: 1, minWidth: 0, minHeight: 0, width: '100%', textAlign: 'center' }}>
          <span data-qs-name style={{ ...nameLines(quickNameLines('text', c.height), true), fontSize: T.name.size, lineHeight: `${T.name.line}px`, fontWeight: 600, color: 'var(--ink-900)' }}>{c.name}</span>
          {a && (
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: T.sizeGap, flex: 'none' }}>
              <span style={{ fontSize: T.price.size, lineHeight: `${T.price.line}px`, fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{a.price}</span>
              {c.note ?? (a.size ? <span style={{ fontSize: B.sizeLabel.size, color: 'var(--ink-500)' }}>{a.size}</span> : null)}
            </span>
          )}
        </span>
      );
    }
    default: {
      // Un dossier en texte seul : le nom, puis la pastille, centrés.
      const P = B.compact;
      return (
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: P.gap, flex: 1, minWidth: 0, minHeight: 0, width: '100%', textAlign: 'center' }}>
          <LevelName c={c} size={P.name.size} line={P.name.line} center lines={quickNameLines('compact', c.height)} balance />
          <CountPill c={c} room={c.width - 2 * (B.border + P.padX)} padX={B.pill.compactPadX} />
        </span>
      );
    }
  }
}
